/**
 * @file        src/api/hotelBookings.js
 * @description Typed admin API client for per-booking B2C Hotel actions:
 *              details, DB-only update, and supplier cancellation (proxied).
 * @author      Kinetika Labs
 * @created     2026-07-16
 * @changelog
 *   - 2026-08-04 Claude — CR D2: added shareByEmail (Voucher/Invoice/Both, with
 *                          real PDF attachments built client-side by
 *                          ShareEmailModal).
 *   - 2026-08-04 Claude — Flight parity: listAmendments + raiseEmailAmendment
 *                          (hotel amendment queue) and getBookingLogs (Timeline).
 *   - 2026-07-16 Claude — Added details + DB-only update (flight-parity actions).
 *   - 2026-07-16 Claude — Initial (Hotel Booking List Cancel action).
 */
import { get, patch, post } from './client';

export const getBookingDetails = (id) => get(`/hotel/bookings/${id}/details`);

export const updateBooking = (id, body) => patch(`/hotel/bookings/${id}`, body);

export const cancelPreview = (id) => post(`/hotel/bookings/${id}/cancel/preview`, {});

export const cancelSubmit = (id, reason) =>
  post(`/hotel/bookings/${id}/cancel/submit`, { reason });

// Amendments raised against a booking (for the Amendments card).
export const listAmendments = (id) => get(`/hotel/bookings/${id}/amendments`);

// Raise an email-only amendment (Date Change, Room Change, Correction, …). The
// server records it and notifies the ops team; the result reports { emailed }.
export const raiseEmailAmendment = (id, body) =>
  post(`/hotel/bookings/${id}/amendment/email`, body);

// Operational history of one booking — the server-composed `timeline` array
// (created / payment / amendment / cancellation / refund lifecycle / admin
// edits, each with its actor and its before → after field changes). Powers the
// Timeline card in the Details drawer.
export const getBookingLogs = (id) => get(`/hotel/bookings/${id}/logs`);

// Email the Voucher / Tax Invoice / Both, with real PDF attachment(s), to the
// customer or an override + CC address (CR D2). `body` = { document, to, cc,
// attachments: [{ filename, contentBase64, documentType }] }.
export const shareByEmail = (id, body) =>
  post(`/hotel/bookings/${id}/share-email`, body);
